import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PartyPopper, Scale, UtensilsCrossed, Search, Loader2, Star, MapPin, Bookmark, BookmarkCheck, CalendarPlus, Navigation } from "lucide-react";
import { api } from "@/api/client";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Three everyday-friendly buckets → server category keys.
const CATEGORIES = [
  { key: "events", label: "Community Events & Festivals", Icon: PartyPopper, color: "#FF9E9E" },
  { key: "services", label: "Helpful Services (Legal/Accounting Aid)", Icon: Scale, color: "#A4DCFA" },
  { key: "dining", label: "Local Dining / Highlights", Icon: UtensilsCrossed, color: "#98FFB6" },
];

// Sensible default centre (Sydney CBD) until the browser shares a location.
const DEFAULT_COORDS = { latitude: -33.8688, longitude: 151.2093 };

export default function LocalDiscovery() {
  const [category, setCategory] = useState("events");
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(DEFAULT_COORDS);
  const [places, setPlaces] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(new Set());
  const [added, setAdded] = useState(new Set());

  useEffect(() => {
    api.get("/local/saved")
      .then((r) => setSaved(new Set((r.data.places || []).map((p) => p.place_id))))
      .catch(() => {});
  }, []);

  const toggleSave = async (p, e) => {
    e?.stopPropagation();
    const isSaved = saved.has(p.place_id);
    // optimistic
    setSaved((prev) => {
      const n = new Set(prev);
      isSaved ? n.delete(p.place_id) : n.add(p.place_id);
      return n;
    });
    try {
      if (isSaved) {
        await api.delete(`/local/saved/${encodeURIComponent(p.place_id)}`);
        toast("Removed from My Saved Places");
      } else {
        await api.post("/local/saved", {
          place_id: p.place_id, name: p.name, address: p.address || "",
          category, primary_type: p.primary_type || "", rating: p.rating,
          latitude: p.latitude, longitude: p.longitude, photo_path: p.photo_path || "",
        });
        toast.success("Saved to My Saved Places");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update your saved places");
      setSaved((prev) => { // revert
        const n = new Set(prev);
        isSaved ? n.add(p.place_id) : n.delete(p.place_id);
        return n;
      });
    }
  };

  const addToTrip = async (p, e) => {
    e?.stopPropagation();
    try {
      await api.post("/relocation/itinerary/add-stop", {
        name: p.name, address: p.address || "", category,
        place_id: p.place_id, latitude: p.latitude, longitude: p.longitude,
      });
      setAdded((prev) => new Set(prev).add(p.place_id));
      toast.success("Added to your Trip Itinerary");
    } catch {
      toast.error("Couldn't add that stop to your trip");
    }
  };

  const directionsUrl = (p) =>
    p.latitude != null && p.longitude != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}${p.place_id ? `&destination_place_id=${encodeURIComponent(p.place_id)}` : ""}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.name} ${p.address || ""}`)}`;

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => {}, { timeout: 5000 }
    );
  }, []);

  const search = async (cat = category) => {
    setLoading(true);
    try {
      const { data } = await api.get("/local/search", {
        params: {
          category: cat, query: query.trim(),
          latitude: coords.latitude, longitude: coords.longitude,
        },
      });
      setPlaces(data.places || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Local search is temporarily unavailable";
      toast.error(typeof msg === "string" ? msg : "Local search failed");
      setPlaces([]);
    } finally { setLoading(false); }
  };

  // Auto-run on category change.
  useEffect(() => { search(category); /* eslint-disable-next-line */ }, [category]);

  const active = CATEGORIES.find((c) => c.key === category);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-3 pb-28" data-testid="local-discovery">
      <div className="mb-3">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/60">Local: What's Going On</p>
        <h2 className="font-serif text-xl text-charcoal">See what's around you</h2>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-2 mb-3">
        <input
          data-testid="local-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder={`Search ${active?.label.toLowerCase() || "places"}…`}
          className="flex-1 min-w-0 bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-[12px] shadow-[2px_2px_0px_#1A1A1A] focus:outline-none"
        />
        <button
          data-testid="local-search-btn" onClick={() => search()} disabled={loading}
          className="shrink-0 h-10 px-4 grid place-items-center rounded-xl border-2 border-charcoal bg-gold text-black font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} strokeWidth={2.6} />}
        </button>
      </div>

      {/* Category toggle */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="local-category-toggle">
        {CATEGORIES.map((c) => {
          const on = c.key === category;
          return (
            <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`local-cat-${c.key}`}
              className={`flex flex-col items-center gap-1.5 rounded-xl border-2 border-charcoal px-2 py-2.5 text-center transition-[transform,box-shadow] ${on ? "shadow-[3px_3px_0px_#1A1A1A]" : "shadow-none opacity-70"}`}
              style={{ backgroundColor: on ? c.color : "#FFFFFF" }}>
              <c.Icon size={18} strokeWidth={2.3} className="text-black" />
              <span className="font-mono text-[8px] font-black uppercase leading-tight tracking-wide text-black">{c.label}</span>
            </button>
          );
        })}
      </div>

      {/* Results */}
      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-charcoal/70" /></div>
      ) : places === null ? null : places.length === 0 ? (
        <p className="font-mono text-[11px] text-charcoal/60 text-center mt-10" data-testid="local-empty">
          Nothing found nearby — try a different search or category.
        </p>
      ) : (
        <div className="space-y-3" data-testid="local-results">
          {places.map((p) => (
            <div key={p.id} data-testid={`local-place-${p.id}`}
              className="overflow-hidden rounded-2xl border-2 border-charcoal bg-white shadow-[3px_3px_0px_#1A1A1A]">
              {p.photo_path && (
                <img src={`${BACKEND}${p.photo_path}`} alt={p.name} loading="lazy"
                  className="h-32 w-full object-cover border-b-2 border-charcoal" />
              )}
              <div className="p-3">
                <p className="font-serif text-base text-charcoal leading-tight">{p.name}</p>
                {p.address && (
                  <p className="mt-1 flex items-start gap-1 font-mono text-[10px] text-charcoal/60">
                    <MapPin size={11} className="mt-0.5 shrink-0" /> {p.address}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-2">
                  {p.primary_type && (
                    <span className="rounded-full bg-charcoal/85 px-2 py-0.5 font-mono text-[8px] font-black uppercase tracking-widest text-gold">
                      {String(p.primary_type).replace(/_/g, " ")}
                    </span>
                  )}
                  {p.rating != null && (
                    <span className="flex items-center gap-1 font-mono text-[10px] font-bold text-charcoal">
                      <Star size={11} className="fill-gold text-gold" /> {p.rating}
                      <span className="text-charcoal/40">({p.review_count ?? 0})</span>
                    </span>
                  )}
                </div>
                {/* Point-and-click actions: Save · Add to Trip · Directions */}
                <div className="mt-3 flex items-center gap-2">
                  <button data-testid={`local-save-${p.id}`} onClick={(e) => toggleSave(p, e)}
                    className={`flex items-center gap-1 rounded-lg border-2 border-charcoal px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${saved.has(p.place_id) ? "bg-gold text-black" : "bg-white text-charcoal"}`}>
                    {saved.has(p.place_id) ? <BookmarkCheck size={12} /> : <Bookmark size={12} />}
                    {saved.has(p.place_id) ? "Saved" : "Save"}
                  </button>
                  <button data-testid={`local-addtrip-${p.id}`} onClick={(e) => addToTrip(p, e)}
                    className={`flex items-center gap-1 rounded-lg border-2 border-charcoal px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${added.has(p.place_id) ? "bg-[#98FFB6] text-black" : "bg-white text-charcoal"}`}>
                    <CalendarPlus size={12} /> {added.has(p.place_id) ? "Added" : "Add to Trip"}
                  </button>
                  <a data-testid={`local-directions-${p.id}`} href={directionsUrl(p)} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-auto flex items-center gap-1 rounded-lg border-2 border-charcoal bg-charcoal px-2.5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-gold shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
                    <Navigation size={12} /> Directions
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
